import InputField from "components/atoms/InputField";
import { BreadcrumbNav } from "components/molecules";
import React, { useEffect, useState } from "react";
import {
  FeedbackSampleOption,
  PageMode,
  PCRResultOptions,
  SampleSendType,
  UserLevel,
  YesAndNoOptions,
} from "utils/constant";
import { useFormik } from "formik";
import InputDate from "components/atoms/InputDate";
import moment from "moment";
import InputSelect from "components/atoms/InputSelect";
import { formatParasite } from "utils/parasite";
import { useSelector } from "react-redux";
import Button from "components/atoms/Button";
import {
  errorToastNotif,
  getDataFromLocalStorage,
  nullConverter,
  successToastNotif,
} from "utils/common";
import UserService from "services/User";
import Skl34 from "services/SurveilansKnowlesiLab34";
import Skl5 from "services/SurveilansKnowlesiLab5";
import { hasSentType, pcrDateError } from "./sampleDateRules";

const { SAD, DBS, WB } = SampleSendType;

const Skl34Service = new Skl34();
const Skl5Service = new Skl5();
const UserServiceService = new UserService();

const FormComponent = ({ changeMode, pageMode }) => {
  const { currentUser } = useSelector((state) => state.authReducer);
  const [isEditable, setisEditable] = useState(true);

  const [selectedData, setSelectedData] = useState(() => {
    const data = getDataFromLocalStorage("SELECTED_ITEM");
    return JSON.parse(data);
  });
  // Jenis sampel yang dikirim dibaca dari sample_send_type (checkbox), bukan dari
  // keberadaan tanggal kirim: tanggal kirim hanya terisi untuk jenis yang
  // dicentang (jenis lain null).
  const hasSad = hasSentType(selectedData, SAD);
  const hasDbs = hasSentType(selectedData, DBS);
  const hasWb = hasSentType(selectedData, WB);

  const [isPcr, setIsPcr] = useState(
    [
      hasSad ? selectedData.sample_sad_condition : null,
      hasDbs ? selectedData.sample_dbs_condition : null,
      hasWb ? selectedData.sample_wb_condition : null,
    ].includes("Ya")
  );
  const [isFinishChecking, setIsFinishChecking] = useState(false);
  const [isLab5, setIsLab5] = useState(
    selectedData.is_send_to_lab_tier_5 === "Ya"
  );
  const [labTier5, setLabTier5] = useState([]);

  const formik = useFormik({
    initialValues: selectedData,

    validate: (values) => {
      const errors = {};
      if (hasSad && !values.sample_sad_received_date) {
        errors.sample_sad_received_date =
          "Tanggal sampel SAD diterima harus diisi";
      }
      const isPcrVisible = [
        hasSad ? values.sample_sad_condition : null,
        hasDbs ? values.sample_dbs_condition : null,
        hasWb ? values.sample_wb_condition : null,
      ].includes("Ya");

      if (isPcrVisible) {
        if (values.pcr_finish_checking_date) {
          if (
            !values.pcr_checking_result ||
            values.pcr_checking_result.length === 0
          ) {
            errors.pcr_checking_result = "Hasil pemeriksaan PCR harus diisi";
          }
        }
        if (values.is_send_to_lab_tier_5 === "Ya" && !values.lab_tier_5_id) {
          errors.lab_tier_5_id = "Lab Tingkat 5 harus diisi";
        }
        const pcrErr = pcrDateError(values);
        if (pcrErr) errors.pcr_finish_checking_date = pcrErr;
      }
      return errors;
    },

    onSubmit: async (values) => {
      try {
        await Skl34Service.update(nullConverter(values), selectedData.id)
          .then(async () => {
            if (
              formik.values.is_send_to_lab_tier_5 === "Ya" &&
              formik.values.lab_tier_5_id
            ) {
              await Skl5Service.add({
                source_lab_id:
                  currentUser.username + "|" + currentUser.first_name,
                destination_lab_id: formik.values.lab_tier_5_id,
                regmal_id: formik.values.regmal_id,
                sample_send_date: moment().format("YYYY-MM-DD"),
              });
            }
          })
          .then(() => {
            changeMode(PageMode.view);
            successToastNotif("Surveilans Knowlesi Lab Berhasil Diperbarui");
          });
      } catch (error) {
        errorToastNotif(error);
      }
    },
  });

  const computeIsPcr = (overrides = {}) => {
    const v = { ...formik.values, ...overrides };
    return [
      hasSad ? v.sample_sad_condition : null,
      hasDbs ? v.sample_dbs_condition : null,
      hasWb ? v.sample_wb_condition : null,
    ].includes("Ya");
  };

  const handleConditionChange = (field, value) => {
    const nextIsPcr = computeIsPcr({ [field]: value });
    setIsPcr(nextIsPcr);
    if (!nextIsPcr) {
      formik.setFieldValue("pcr_finish_checking_date", "");
      formik.setFieldValue("pcr_checking_result", []);
      formik.setFieldValue("is_send_to_lab_tier_5", "");
      formik.setFieldValue("lab_tier_5_id", "");
    }
  };

  const handleIsLab5Change = (value) => {
    setIsLab5(value === "Ya");

    if (value === "Tidak") {
      formik.setFieldValue("lab_tier_5_id", "");
    }
  };

  useEffect(async () => {
    const data = await UserServiceService.getAll({
      page: 1,
      limit: 9999,
      searchBy: "level",
      searchValue: UserLevel.LabTier5,
    });
    const mappedData = data.users.map((item) => {
      return {
        label: item.first_name,
        value: item.username + "|" + item.first_name,
      };
    });
    setLabTier5(mappedData);

    await Skl5Service.getAll({
      page: 1,
      limit: 1,
      searchBy: "regmal_id",
      searchValue: selectedData.regmal_id,
    }).then((res) => {
      if (res.total > 0) {
        setisEditable(false);
      }
    });
  }, []);

  return (
    <>
      <BreadcrumbNav
        items={[
          {
            label: "Knowlesi Lab",
            onClick: () => changeMode(PageMode.view),
          },
          {
            label: "Edit Data Knowlesi Lab",
          },
        ]}
      />

      <div className="informasi-wilayah">
        <section className="list-item">
          <p className="item-label">Provinsi</p>
          <p className="item-value">{selectedData?.province_name}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Kab/Kota</p>
          <p className="item-value">{selectedData?.district_name}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Faskes Pencatat</p>
          <p className="item-value">{selectedData?.fasyankes_name}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Kode Slide</p>
          <p className="item-value">{selectedData?.slide_code}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Nama pasien</p>
          <p className="item-value">{selectedData?.patient_name}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Alamat Domisili Pasien</p>
          <p className="item-value">{selectedData?.address}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Pekerjaan pasien</p>
          <p className="item-value">{selectedData?.job}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Jenis Parasit</p>
          <p className="item-value">{formatParasite(selectedData?.parasyte)}</p>
        </section>
        <section className="list-item">
          <p className="item-label">Faskes Pengirim Sampel</p>
          <p className="item-value">
            {selectedData?.source_fasyankes_id.split("|")[1]}
          </p>
        </section>
        <section className="list-item">
          <p className="item-label">
            Tanggal Sampel Sediaan Apusan Darah (SAD) dikirim
          </p>
          <p className="item-value">
            {hasSad ? selectedData?.sample_sad_send_date || "-" : "-"}
          </p>
        </section>
        <section className="list-item">
          <p className="item-label">
            Tanggal Sampel Dried Blood Spot (DBS) dikirim
          </p>
          <p className="item-value">
            {hasDbs ? selectedData?.sample_dbs_send_date || "-" : "-"}
          </p>
        </section>
        <section className="list-item">
          <p className="item-label">
            Tanggal Sampel Whole Blood EDTA (WB) dikirim
          </p>
          <p className="item-value">
            {hasWb ? selectedData?.sample_wb_send_date || "-" : "-"}
          </p>
        </section>
      </div>

      <div className="form-grouping">
        <p
          className="warning"
          style={{ marginTop: "24px", marginBottom: "24px" }}
        >
          Step 1
        </p>

        {hasSad && (
          <>
            <InputDate
              label={"Tanggal Sampel Sediaan Apusan Darah (SAD) diterima"}
              name={"sample_sad_received_date"}
              placeholder={"Pilih Tanggal sampel diterima"}
              dateFormat={"YYYY-MM-DD"}
              onChange={(e) => {
                const selectedDate = moment(e).format("YYYY-MM-DD");
                formik.handleChange("sample_sad_received_date")(selectedDate);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.sample_sad_received_date}
              error={
                formik.errors.sample_sad_received_date &&
                formik.touched.sample_sad_received_date
              }
              errorMessage={formik.errors.sample_sad_received_date}
              isRequired={hasSad}
              isDisabled={!isEditable}
              isValidDate={(currentDate) =>
                !selectedData?.sample_sad_send_date ||
                moment(currentDate).isSameOrAfter(
                  moment(selectedData.sample_sad_send_date)
                )
              }
            />

            <InputSelect
              label={
                "Kondisi Sampel Sediaan Apusan Darah (SAD) (Memenuhi syarat pemeriksaan PCR)"
              }
              name={"sample_sad_condition"}
              placeholder={"Pilih Kondisi sampel"}
              data={YesAndNoOptions}
              onChange={(e) => {
                const selectedValue = e ? e.value : "";
                handleConditionChange("sample_sad_condition", selectedValue);
                formik.handleChange("sample_sad_condition")(selectedValue);
              }}
              onBlur={formik.handleBlur}
              value={YesAndNoOptions.find(
                (item) => item.value === formik.values.sample_sad_condition
              )}
              error={
                formik.errors.sample_sad_condition &&
                formik.touched.sample_sad_condition
              }
              errorMessage={formik.errors.sample_sad_condition}
              isRequired={hasSad}
              isDisabled={!isEditable}
              isClearable={false}
            />
            <InputSelect
              label={"Umpan Balik Kondisi Sampel Sediaan Apusan Darah (SAD)"}
              name={"sample_sad_feedback"}
              placeholder={"Umpan Balik Kondisi Sampel"}
              data={FeedbackSampleOption}
              onChange={(e) => {
                const selectedValue = e ? e.value : "";
                formik.handleChange("sample_sad_feedback")(selectedValue);
              }}
              onBlur={formik.handleBlur}
              value={FeedbackSampleOption.find(
                (item) => item.value === formik.values.sample_sad_feedback
              )}
              error={
                formik.errors.sample_sad_feedback &&
                formik.touched.sample_sad_feedback
              }
              errorMessage={formik.errors.sample_sad_feedback}
              isRequired={false}
              isDisabled={!isEditable}
              isClearable={false}
            />
          </>
        )}

        {hasDbs && (
          <>
            <InputDate
              label={"Tanggal Sampel Dried Blood Spot (DBS) diterima"}
              name={"sample_dbs_received_date"}
              placeholder={"Pilih Tanggal sampel diterima"}
              dateFormat={"YYYY-MM-DD"}
              onChange={(e) => {
                const selectedDate = moment(e).format("YYYY-MM-DD");
                formik.handleChange("sample_dbs_received_date")(selectedDate);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.sample_dbs_received_date}
              error={
                formik.errors.sample_dbs_received_date &&
                formik.touched.sample_dbs_received_date
              }
              errorMessage={formik.errors.sample_dbs_received_date}
              isRequired={false}
              isDisabled={!isEditable}
              isValidDate={(currentDate) =>
                !selectedData?.sample_dbs_send_date ||
                moment(currentDate).isSameOrAfter(
                  moment(selectedData.sample_dbs_send_date)
                )
              }
            />

            <InputSelect
              label={
                "Kondisi Sampel Dried Blood Spot (DBS) (Memenuhi syarat pemeriksaan PCR)"
              }
              name={"sample_dbs_condition"}
              placeholder={"Pilih Kondisi sampel"}
              data={YesAndNoOptions}
              onChange={(e) => {
                const selectedValue = e ? e.value : "";
                handleConditionChange("sample_dbs_condition", selectedValue);
                formik.handleChange("sample_dbs_condition")(selectedValue);
              }}
              onBlur={formik.handleBlur}
              value={YesAndNoOptions.find(
                (item) => item.value === formik.values.sample_dbs_condition
              )}
              error={
                formik.errors.sample_dbs_condition &&
                formik.touched.sample_dbs_condition
              }
              errorMessage={formik.errors.sample_dbs_condition}
              isRequired={hasDbs}
              isDisabled={!isEditable}
              isClearable={false}
            />

            <InputField
              label={"Umpan Balik Kondisi Sampel Dried Blood Spot (DBS)"}
              name={"sample_dbs_feedback"}
              placeholder={"Umpan Balik Kondisi Sampel"}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.sample_dbs_feedback}
              error={
                formik.errors.sample_dbs_feedback &&
                formik.touched.sample_dbs_feedback
              }
              errorMessage={formik.errors.sample_dbs_feedback}
              isRequired={false}
              disabled={!isEditable}
            />
          </>
        )}

        {hasWb && (
          <>
            <InputDate
              label={"Tanggal Sampel Whole Blood EDTA (WB) diterima"}
              name={"sample_wb_received_date"}
              placeholder={"Pilih Tanggal sampel diterima"}
              dateFormat={"YYYY-MM-DD"}
              onChange={(e) => {
                const selectedDate = moment(e).format("YYYY-MM-DD");
                formik.handleChange("sample_wb_received_date")(selectedDate);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.sample_wb_received_date}
              error={
                formik.errors.sample_wb_received_date &&
                formik.touched.sample_wb_received_date
              }
              errorMessage={formik.errors.sample_wb_received_date}
              isRequired={false}
              isDisabled={!isEditable}
              isValidDate={(currentDate) =>
                !selectedData?.sample_wb_send_date ||
                moment(currentDate).isSameOrAfter(
                  moment(selectedData.sample_wb_send_date)
                )
              }
            />

            <InputSelect
              label={
                "Kondisi Sampel Whole Blood EDTA (WB) (Memenuhi syarat pemeriksaan PCR)"
              }
              name={"sample_wb_condition"}
              placeholder={"Pilih Kondisi sampel"}
              data={YesAndNoOptions}
              onChange={(e) => {
                const selectedValue = e ? e.value : "";
                handleConditionChange("sample_wb_condition", selectedValue);
                formik.handleChange("sample_wb_condition")(selectedValue);
              }}
              onBlur={formik.handleBlur}
              value={YesAndNoOptions.find(
                (item) => item.value === formik.values.sample_wb_condition
              )}
              error={
                formik.errors.sample_wb_condition &&
                formik.touched.sample_wb_condition
              }
              errorMessage={formik.errors.sample_wb_condition}
              isRequired={hasWb}
              isDisabled={!isEditable}
              isClearable={false}
            />

            <InputField
              label={"Umpan Balik Kondisi Sampel Whole Blood EDTA (WB)"}
              name={"sample_wb_feedback"}
              placeholder={"Umpan Balik Kondisi Sampel"}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.sample_wb_feedback}
              error={
                formik.errors.sample_wb_feedback &&
                formik.touched.sample_wb_feedback
              }
              errorMessage={formik.errors.sample_wb_feedback}
              isRequired={false}
              disabled={!isEditable}
            />
          </>
        )}

        {isPcr && (
          <>
            <p
              className="warning"
              style={{ marginTop: "24px", marginBottom: "24px" }}
            >
              Step 2
            </p>
            <div className="row">
              <div className="col-sm-6">
                <InputDate
                  label={"Tanggal selesai pemeriksaan PCR"}
                  name={"pcr_finish_checking_date"}
                  placeholder={"Pilih Tanggal selesai pemeriksaan PCR"}
                  dateFormat={"YYYY-MM-DD"}
                  onChange={(e) => {
                    const selectedDate = moment(e).format("YYYY-MM-DD");
                    formik.handleChange("pcr_finish_checking_date")(
                      selectedDate
                    );
                    setIsFinishChecking(true);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.pcr_finish_checking_date}
                  error={
                    formik.errors.pcr_finish_checking_date &&
                    formik.touched.pcr_finish_checking_date
                  }
                  errorMessage={formik.errors.pcr_finish_checking_date}
                  isRequired={false}
                  isDisabled={!isEditable}
                  isValidDate={(currentDate) =>
                    !pcrDateError({
                      ...formik.values,
                      pcr_finish_checking_date:
                        moment(currentDate).format("YYYY-MM-DD"),
                    })
                  }
                />
              </div>
              <div className="col-sm-6">
                <InputSelect
                  label={"Hasil Pemeriksaan PCR"}
                  name={"pcr_checking_result"}
                  placeholder={"Pilih Hasil Pemeriksaan PCR"}
                  data={PCRResultOptions}
                  italicizeParasite
                  isMultipleSelect
                  onChange={(selected) => {
                    const selectedValues = Array.isArray(selected)
                      ? selected.map((item) => item.value)
                      : [];
                    formik.setFieldValue(
                      "pcr_checking_result",
                      selectedValues
                    );
                  }}
                  onBlur={formik.handleBlur}
                  value={PCRResultOptions.filter((item) =>
                    (formik.values.pcr_checking_result || []).includes(
                      item.value
                    )
                  )}
                  error={
                    formik.errors.pcr_checking_result &&
                    formik.touched.pcr_checking_result
                  }
                  errorMessage={formik.errors.pcr_checking_result}
                  isRequired={isFinishChecking}
                  isDisabled={!isEditable}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6">
                <InputSelect
                  label={"Apakah sampel dikirim ke Laboratorium Tingkat 5?"}
                  name={"is_send_to_lab_tier_5"}
                  placeholder={"Pilih"}
                  data={YesAndNoOptions}
                  onChange={(e) => {
                    handleIsLab5Change(e.value);
                    const selectedValue = e ? e.value : "";
                    formik.handleChange("is_send_to_lab_tier_5")(selectedValue);
                  }}
                  onBlur={formik.handleBlur}
                  value={YesAndNoOptions.find(
                    (item) => item.value === formik.values.is_send_to_lab_tier_5
                  )}
                  error={
                    formik.errors.is_send_to_lab_tier_5 &&
                    formik.touched.is_send_to_lab_tier_5
                  }
                  errorMessage={formik.errors.is_send_to_lab_tier_5}
                  isRequired={false}
                  isDisabled={!isEditable}
                />
              </div>
              <div className="col-sm-6">
                <InputSelect
                  label={"Jika Ya, Sebutkan Labnya?"}
                  name={"lab_tier_5_id"}
                  placeholder={"Pilih Lab Tingkat 5"}
                  data={labTier5}
                  onChange={(e) => {
                    const selectedValue = e && e.value ? e.value : "";
                    formik.setFieldValue("lab_tier_5_id", selectedValue);
                  }}
                  onBlur={formik.handleBlur}
                  value={labTier5.find(
                    (item) => item.value === formik.values.lab_tier_5_id
                  )}
                  error={
                    formik.errors.lab_tier_5_id && formik.touched.lab_tier_5_id
                  }
                  errorMessage={formik.errors.lab_tier_5_id}
                  isRequired={isLab5}
                  disabled={!isLab5 || !isEditable}
                />
              </div>
            </div>
          </>
        )}
        <div className="form_action">
          <Button
            variant={"tertiary"}
            size={"normal"}
            onClick={() => changeMode(PageMode.view)}
          >
            Batalkan
          </Button>
          <Button
            variant={"primary"}
            size={"normal"}
            onClick={formik.handleSubmit}
            disabled={!(formik.isValid && formik.dirty) || !isEditable}
          >
            Simpan Data
          </Button>
        </div>
      </div>
    </>
  );
};

export default FormComponent;
